import React, { useState } from "react";
import FormUI from '@/componentsUI/Form';
import PopupMask from "@/componentsUI/PopupMask";
import ButtonUI from "@/componentsUI/Button";
import InputUI from "@/componentsUI/Input";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import { feedback } from "@/utils/feedback";
import { WITHDRAW_EMAIL_MAX_LENGTH } from "../../constants";
import styles from "./index.module.less";

export interface SenegalWithdrawConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: {
    firstName: string;
    lastName: string;
    email?: string;
  }) => void;
  withdrawData?: any; // 传入的提现数据
  loading?: boolean;
}

const SenegalWithdrawConfirmModal: React.FC<
  SenegalWithdrawConfirmModalProps
> = ({ visible, onClose, onConfirm, loading = false }) => {
  const { t } = useTranslationPlus("Wallet");
  const [form] = FormUI.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    form
      .validate()
      .then((values) => {
        setIsSubmitting(true);
        const confirmData = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        };

        // 调用确认回调
        onConfirm(confirmData);
      })
      .catch((error) => {
        console.log("Validation failed:", error);
        feedback.error(t("Please fill in all required fields"));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    setIsSubmitting(false);
    onClose();
  };

  return (
    <PopupMask visible={visible} onClose={handleCancel}>
      <div className={styles["senegal-withdraw-confirm-modal"]}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t("Confirm Withdrawal")}</h3>
          <button className={styles.closeBtn} onClick={handleCancel}>
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.infoSection}>
            <p className={styles.infoText}>
              {t(
                "Please provide the following information to complete your withdrawal:"
              )}
            </p>
          </div>

          <div className={styles.formSection}>
            <FormUI form={form} layout="vertical" autoComplete="off">
              <FromItemPlus
                field="firstName"
                label={t("First Name")}
                className={styles.formItem}
                rules={[
                  {
                    required: true,
                    message: t("Please enter your first name"),
                  },
                  {
                    minLength: 2,
                    message: t("First name must be at least 2 characters"),
                  },
                ]}
              >
                <InputUI
                  placeholder={t("Enter your first name")}
                  className={styles.formInput}
                />
              </FromItemPlus>

              <FromItemPlus
                field="lastName"
                label={t("Last Name")}
                className={styles.formItem}
                rules={[
                  {
                    required: true,
                    message: t("Please enter your last name"),
                  },
                  {
                    minLength: 2,
                    message: t("Last name must be at least 2 characters"),
                  },
                ]}
              >
                <InputUI
                  placeholder={t("Enter your last name")}
                  className={styles.formInput}
                />
              </FromItemPlus>

              <FromItemPlus
                field="email"
                label={t("Email Address") + " (" + t("Optional") + ")"}
                className={styles.formItem}
                rules={[
                  {
                    type: "email",
                    message: t("Please enter a valid email address"),
                    validator: (value, callback) => {
                      if (value && value.trim() !== "") {
                        // 只有在有值的情况下才验证邮箱格式
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(value)) {
                          return callback(
                            t("Please enter a valid email address")
                          );
                        }
                      }
                      return callback();
                    },
                  },
                ]}
              >
                <InputUI
                  placeholder={
                    t("Enter your email address") + " (" + t("Optional") + ")"
                  }
                  maxLength={WITHDRAW_EMAIL_MAX_LENGTH}
                  className={styles.formInput}
                />
              </FromItemPlus>
            </FormUI>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <ButtonUI className={styles.cancelBtn} onClick={handleCancel}>
            {t("Cancel")}
          </ButtonUI>
          <ButtonUI
            type="primary"
            className={styles.confirmBtn}
            onClick={handleConfirm}
            loading={loading || isSubmitting}
            disabled={loading || isSubmitting}
          >
            {t("Confirm Withdrawal")}
          </ButtonUI>
        </div>
      </div>
    </PopupMask>
  );
};

export default SenegalWithdrawConfirmModal;
